import { predefinedCustomFormulaFunctions } from "./customFormulaFunctions";
import type { FormulaReferenceField } from "./customFormulaParser";
import { findUnescapedQuoteForward, unescapeFormulaQuotedValue } from "./customFormulaQuotedValue";

export type FormulaHighlightType = "none" | "field" | "function";

export interface FormulaHighlightSegment {
  text: string;
  highlighted: boolean;
  highlightType: FormulaHighlightType;
}

const buildReferenceLookup = (referenceFields: FormulaReferenceField[]): Set<string> => {
  const values = new Set<string>();

  referenceFields.forEach((field) => {
    values.add(field.caption.toLowerCase());
    values.add(field.guid.toLowerCase());
  });

  return values;
};

const isKnownFieldReference = (value: string, referenceLookup: Set<string>): boolean => {
  return referenceLookup.has(value.toLowerCase());
};

const buildFunctionLookup = (functions: string[]): Map<string, string> => {
  const lookup = new Map<string, string>();
  functions.forEach((fn) => {
    lookup.set(fn.toLowerCase(), fn);
  });
  return lookup;
};

const splitTextWithFunctionHighlights = (
  text: string,
  functionLookup: Map<string, string>
): FormulaHighlightSegment[] => {
  if (functionLookup.size === 0) {
    return [{ text, highlighted: false, highlightType: "none" }];
  }

  const pattern = new RegExp(
    `\\b(${Array.from(functionLookup.values())
      .map((fn) => fn.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|")})\\b`,
    "gi"
  );

  const segments: FormulaHighlightSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), highlighted: false, highlightType: "none" });
    }
    segments.push({ text: match[0], highlighted: true, highlightType: "function" });
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), highlighted: false, highlightType: "none" });
  }

  return segments;
};

export const splitFormulaHighlightSegments = (
  formulaInput: string,
  referenceFields: FormulaReferenceField[]
): FormulaHighlightSegment[] => {
  if (formulaInput.length === 0) {
    return [];
  }

  const referenceLookup = buildReferenceLookup(referenceFields);
  const functionLookup = buildFunctionLookup(predefinedCustomFormulaFunctions);
  const segments: FormulaHighlightSegment[] = [];
  let index = 0;

  while (index < formulaInput.length) {
    const quoteStartIndex = findUnescapedQuoteForward(formulaInput, index);
    if (quoteStartIndex < 0) {
      segments.push(...splitTextWithFunctionHighlights(formulaInput.slice(index), functionLookup));
      break;
    }

    if (quoteStartIndex > index) {
      segments.push(...splitTextWithFunctionHighlights(formulaInput.slice(index, quoteStartIndex), functionLookup));
    }

    const quoteEndIndex = findUnescapedQuoteForward(formulaInput, quoteStartIndex + 1);
    if (quoteEndIndex < 0) {
      segments.push({ text: formulaInput.slice(quoteStartIndex), highlighted: false, highlightType: "none" });
      break;
    }

    const quotedReference = formulaInput.slice(quoteStartIndex + 1, quoteEndIndex);
    const unescapedReference = unescapeFormulaQuotedValue(quotedReference);
    const highlighted =
      unescapedReference.value !== undefined && isKnownFieldReference(unescapedReference.value, referenceLookup);
    segments.push({
      text: formulaInput.slice(quoteStartIndex, quoteEndIndex + 1),
      highlighted,
      highlightType: highlighted ? "field" : "none",
    });

    index = quoteEndIndex + 1;
  }

  return segments;
};
